import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const ComplianceAlertBanner = () => {
  const [alerts, setAlerts] = useState([]);
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Simulate real-time compliance alerts
    const mockAlerts = [
      {
        id: 1,
        type: 'warning',
        title: 'EUA Allowance Shortage',
        message: 'Fleet requires 2,450 additional EUA allowances for Q4 compliance',
        action: 'Trade EUA',
        actionPath: '/eua-trading-hub',
        deadline: '15 days remaining',
        priority: 'high'
      },
      {
        id: 2,
        type: 'error',
        title: 'D/E Rating Risk',
        message: 'MV Atlantic Voyager approaching D rating threshold',
        action: 'View Report',
        actionPath: '/compliance-reports',
        deadline: '3 days to submit',
        priority: 'critical'
      },
      {
        id: 3,
        type: 'info',
        title: 'Regulatory Update',
        message: 'New IMO guidelines effective January 2025',
        action: 'Review Changes',
        actionPath: '/compliance-reports',
        deadline: 'Review by Dec 31',
        priority: 'medium'
      }
    ];

    setAlerts(mockAlerts);
  }, []);

  useEffect(() => {
    if (alerts?.length > 1) {
      const interval = setInterval(() => {
        setCurrentAlertIndex((prev) => (prev + 1) % alerts?.length);
      }, 8000);

      return () => clearInterval(interval);
    }
  }, [alerts?.length]);

  const getAlertStyles = (type) => {
    switch (type) {
      case 'error':
        return {
          bg: 'bg-error/10 border-error/20',
          text: 'text-error',
          icon: 'AlertCircle'
        };
      case 'warning':
        return {
          bg: 'bg-warning/10 border-warning/20',
          text: 'text-warning',
          icon: 'AlertTriangle'
        };
      case 'info':
        return {
          bg: 'bg-accent/10 border-accent/20',
          text: 'text-accent',
          icon: 'Info'
        };
      default:
        return {
          bg: 'bg-muted border-border',
          text: 'text-muted-foreground',
          icon: 'Bell'
        };
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  const handlePrevious = () => {
    setCurrentAlertIndex((prev) => 
      prev === 0 ? alerts?.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentAlertIndex((prev) => (prev + 1) % alerts?.length);
  };

  if (isDismissed || alerts?.length === 0) {
    return null;
  }

  const currentAlert = alerts?.[currentAlertIndex];
  const alertStyles = getAlertStyles(currentAlert?.type);

  return (
    <div className={`fixed top-16 left-0 right-0 z-1100 border-b ${alertStyles?.bg} animate-slide-in`}>
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center space-x-4 flex-1">
          <Icon 
            name={alertStyles?.icon} 
            size={20} 
            strokeWidth={2}
            className={alertStyles?.text}
          />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3">
              <h4 className="text-sm font-semibold text-foreground">
                {currentAlert?.title}
              </h4>
              <span className={`text-xs px-2 py-1 rounded-full ${alertStyles?.bg} ${alertStyles?.text}`}>
                {currentAlert?.deadline}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {currentAlert?.message}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {alerts?.length > 1 && (
            <div className="flex items-center space-x-1">
              <button
                onClick={handlePrevious}
                className="p-1 hover:bg-muted rounded transition-maritime"
              >
                <Icon name="ChevronLeft" size={16} strokeWidth={2} />
              </button>
              
              <span className="text-xs text-muted-foreground px-2">
                {currentAlertIndex + 1} of {alerts?.length}
              </span>
              
              <button
                onClick={handleNext}
                className="p-1 hover:bg-muted rounded transition-maritime"
              >
                <Icon name="ChevronRight" size={16} strokeWidth={2} />
              </button>
            </div>
          )}

          <Link to={currentAlert?.actionPath}>
            <Button variant="outline" size="sm">
              {currentAlert?.action}
            </Button>
          </Link>

          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-muted rounded transition-maritime"
          >
            <Icon name="X" size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
      {alerts?.length > 1 && (
        <div className="flex justify-center pb-2">
          <div className="flex space-x-1">
            {alerts?.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentAlertIndex(index)}
                className={`w-2 h-2 rounded-full transition-maritime ${
                  index === currentAlertIndex 
                    ? alertStyles?.text?.replace('text-', 'bg-')
                    : 'bg-muted-foreground/30'
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceAlertBanner;